import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Node } from '@tiptap/core';
import { GhostSuggestions } from '../extensions/GhostSuggestions';
import { CoPilotGhostText } from '../extensions/CoPilotGhostText';
import { SlashCommands } from '../extensions/SlashCommands';
import { ContextualPlaceholder } from '../extensions/ContextualPlaceholder';
import { ResizableImage } from '../extensions/ResizableImage';
import { ImagePlaceholder } from '../extensions/ImagePlaceholder';
import { PlaceholderChip } from '../extensions/PlaceholderChip';
import { FloatingToolbar } from './FloatingToolbar';
import { EditorToolbar } from './EditorToolbar';
import { ImageUploadModal } from '../components/modal/ImageUploadModal';
import { useImageUpload } from '../hooks/useImageUpload';
import { VideoUploadModal } from '../components/modal/VideoUploadModal';
import { useVideoUpload } from '../hooks/useVideoUpload';
// Simple iframe extension that TipTap will recognize
const IframeExtension = Node.create({
    name: 'iframe',
    group: 'block',
    atom: true,
    addAttributes() {
        return {
            src: { default: null },
            style: { default: null },
            allowfullscreen: { default: 'true' },
            frameborder: { default: '0' },
            loading: { default: 'lazy' },
            title: { default: 'Embedded video' },
        };
    },
    parseHTML() {
        return [{ tag: 'iframe' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['iframe', HTMLAttributes];
    },
});
// Video wrapper extension
const VideoWrapper = Node.create({
    name: 'videoWrapper',
    group: 'block',
    content: 'iframe',
    addAttributes() {
        return {
            class: { default: 'video-embed' },
            style: { default: 'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; margin: 1rem 0; border-radius: 8px; background: #f3f4f6;' },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'div[class*="video-embed"]',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', HTMLAttributes, 0];
    },
});
export const TipTapEditor = ({ content = '', onChange, onBlur, onReady, placeholder = 'Start writing...', className = '', editable = true, autoFocus = false, getSuggestion, showFloatingToolbar = true, showStaticToolbar = false, enableSlashCommands = true, enableAISuggestions = true, uploadImage, onImageUploadStateChange, onAIRewrite, enablePlaceholderChips = false, editorRef: externalEditorRef, ghostText, ghostMode, onAcceptGhost, onDismissGhost, onRetryGhost, }) => {
    // Refs so the extension's option callbacks (created once at editor init) see
    // the latest handlers without re-creating the editor on every prop change.
    const onAcceptGhostRef = useRef(onAcceptGhost);
    const onDismissGhostRef = useRef(onDismissGhost);
    const onRetryGhostRef = useRef(onRetryGhost);
    useEffect(() => {
        onAcceptGhostRef.current = onAcceptGhost;
        onDismissGhostRef.current = onDismissGhost;
        onRetryGhostRef.current = onRetryGhost;
    }, [onAcceptGhost, onDismissGhost, onRetryGhost]);
    const contentOnFocus = useRef(content);
    // Track the last content we set to prevent infinite loops
    const lastSetContentRef = useRef(content);
    // Guard: suppress onUpdate while a programmatic setContent microtask is pending
    const pendingContentRef = useRef(null);
    // Guard: TipTap fires onUpdate for ProseMirror's initial empty-doc normalization
    // BEFORE onCreate runs, so the lastSetContentRef snapshot in onCreate is too late.
    // Skip the very first onUpdate (it's normalization, not a user keystroke).
    const hasReceivedFirstUpdateRef = useRef(false);
    // Keep refs so editorProps handlers (created once by useEditor) access the latest values
    const uploadImageRef = useRef(uploadImage);
    useEffect(() => {
        uploadImageRef.current = uploadImage;
    }, [uploadImage]);
    const editorRef = useRef(null);
    // Track in-flight upload count so parents can block submission while uploads are pending
    const inflightUploadsRef = useRef(0);
    const onImageUploadStateChangeRef = useRef(onImageUploadStateChange);
    useEffect(() => {
        onImageUploadStateChangeRef.current = onImageUploadStateChange;
    }, [onImageUploadStateChange]);
    // Counter for unique placeholder IDs across uploads within this editor instance
    const uploadIdCounterRef = useRef(0);
    /**
     * Find and remove an imagePlaceholder node by its uploadId attribute.
     */
    const removePlaceholder = useCallback((ed, uploadId) => {
        const { doc, tr } = ed.state;
        let found = false;
        doc.descendants((node, pos) => {
            if (found)
                return false;
            if (node.type.name === 'imagePlaceholder' && node.attrs.uploadId === uploadId) {
                tr.delete(pos, pos + node.nodeSize);
                found = true;
                return false;
            }
        });
        if (found) {
            ed.view.dispatch(tr);
        }
    }, []);
    /**
     * Replace an imagePlaceholder node with a real image node.
     */
    const replacePlaceholderWithImage = useCallback((ed, uploadId, src) => {
        const { doc, tr } = ed.state;
        let found = false;
        doc.descendants((node, pos) => {
            if (found)
                return false;
            if (node.type.name === 'imagePlaceholder' && node.attrs.uploadId === uploadId) {
                const imageNode = ed.schema.nodes.image.create({ src });
                tr.replaceWith(pos, pos + node.nodeSize, imageNode);
                found = true;
                return false;
            }
        });
        if (found) {
            ed.view.dispatch(tr);
        }
    }, []);
    /**
     * Upload an image file dropped/pasted into the editor.
     * Inserts a placeholder node with a loading indicator first, then replaces
     * it with the real image on success or removes it on failure.
     */
    const handleImageFileDrop = useCallback((file) => {
        const ed = editorRef.current;
        if (!ed || !uploadImageRef.current)
            return;
        // Insert a placeholder at the current cursor position
        uploadIdCounterRef.current += 1;
        const uploadId = `upload-${uploadIdCounterRef.current}`;
        ed.chain().focus().insertContent({ type: 'imagePlaceholder', attrs: { uploadId } }).run();
        inflightUploadsRef.current += 1;
        onImageUploadStateChangeRef.current?.(true);
        uploadImageRef.current(file)
            .then((url) => {
            // Replace the placeholder with the real image
            replacePlaceholderWithImage(ed, uploadId, url);
        })
            .catch((err) => {
            console.error('[TipTapEditor] Image drop/paste upload failed:', err);
            // Remove the placeholder on failure
            removePlaceholder(ed, uploadId);
        })
            .finally(() => {
            inflightUploadsRef.current -= 1;
            if (inflightUploadsRef.current <= 0) {
                inflightUploadsRef.current = 0;
                onImageUploadStateChangeRef.current?.(false);
            }
        });
    }, [removePlaceholder, replacePlaceholderWithImage]);
    // Store the current image callback
    const imageCallbackRef = useRef(null);
    const handleImageUpload = (callback) => {
        imageCallbackRef.current = callback;
        openImageModal();
    };
    const handleImageSelectWithCallback = (imageUrl) => {
        if (imageCallbackRef.current) {
            imageCallbackRef.current(imageUrl);
            imageCallbackRef.current = null;
        }
        closeImageModal();
    };
    const handleCloseImageModal = () => {
        imageCallbackRef.current = null;
        closeImageModal();
    };
    // Image upload modal state
    const { isOpen: isImageModalOpen, openModal: openImageModal, closeModal: closeImageModal } = useImageUpload({
        onImageSelect: handleImageSelectWithCallback,
        // Note: cleanup is handled in handleCloseImageModal, not via onCancel
    });
    // Store the current video callback
    const videoCallbackRef = useRef(null);
    const handleVideoUpload = (callback) => {
        videoCallbackRef.current = callback;
        openVideoModal();
    };
    const handleVideoSelectWithCallback = (videoUrl) => {
        if (videoCallbackRef.current) {
            videoCallbackRef.current(videoUrl);
            videoCallbackRef.current = null;
        }
        closeVideoModal();
    };
    const handleCloseVideoModal = () => {
        videoCallbackRef.current = null;
        closeVideoModal();
    };
    // Video upload modal state
    const { isOpen: isVideoModalOpen, openModal: openVideoModal, closeModal: closeVideoModal } = useVideoUpload({
        onVideoSelect: handleVideoSelectWithCallback,
        // Note: cleanup is handled in handleCloseVideoModal, not via onCancel
    });
    const editor = useEditor({
        // Prevent flushSync warning in React Strict Mode by deferring initial render
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                // Make sure StarterKit doesn't strip out iframe content
                dropcursor: {
                    color: '#DBEAFE',
                    width: 4,
                },
                gapcursor: false,
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
            }),
            Placeholder.configure({
                placeholder,
            }),
            ContextualPlaceholder,
            TextStyle,
            Color,
            Underline,
            Highlight.configure({
                multicolor: true,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary underline cursor-pointer',
                },
            }),
            ResizableImage,
            ImagePlaceholder,
            ...(enablePlaceholderChips ? [PlaceholderChip] : []),
            IframeExtension,
            VideoWrapper,
            ...(enableSlashCommands ? [SlashCommands.configure({
                    onImageUpload: handleImageUpload,
                    onVideoUpload: handleVideoUpload,
                })] : []),
            ...(getSuggestion && enableAISuggestions ? [GhostSuggestions.configure({
                    triggerCharacter: '\\',
                    getSuggestion,
                })] : []),
            CoPilotGhostText.configure({
                onAccept: (text) => onAcceptGhostRef.current?.(text),
                onDismiss: () => onDismissGhostRef.current?.(),
                onRetry: () => onRetryGhostRef.current?.(),
            }),
        ],
        content,
        editable,
        autofocus: autoFocus ? 'start' : false,
        onCreate: ({ editor }) => {
            lastSetContentRef.current = editor.getHTML();
            if (onReady) {
                onReady();
            }
        },
        onUpdate: ({ editor }) => {
            if (onChange) {
                if (pendingContentRef.current !== null) {
                    // A programmatic setContent IS the initialization the first-update
                    // allowance exists for, so spend it here. Returning without spending
                    // it left the allowance to be eaten by the operator's first real
                    // keystroke instead — silently, because onChange never fired. In the
                    // article editor (which mounts empty and fills the body once the
                    // fetch lands) that meant a single-character edit followed by Save
                    // posted the unedited body back, with no error anywhere.
                    hasReceivedFirstUpdateRef.current = true;
                    return;
                }
                // The first onUpdate after editor creation CAN be ProseMirror's
                // empty-doc normalization ('' → '<p></p>'), which is not a real edit.
                // Swallow only that — a document with any content in it is a real
                // edit, and swallowing it loses the operator's work.
                if (!hasReceivedFirstUpdateRef.current) {
                    hasReceivedFirstUpdateRef.current = true;
                    // `editor.isEmpty`, not a text check on the HTML: an image, video or
                    // embed carries no text, so stripping tags would classify a first edit
                    // that inserts one as normalization and swallow it.
                    if (editor.isEmpty) {
                        lastSetContentRef.current = editor.getHTML();
                        return;
                    }
                }
                const newContent = editor.getHTML();
                if (newContent === lastSetContentRef.current) {
                    return;
                }
                lastSetContentRef.current = newContent;
                onChange(newContent);
            }
        },
        onFocus: ({ editor }) => {
            contentOnFocus.current = editor.getHTML();
        },
        onBlur: ({ editor }) => {
            if (onBlur && editor.getHTML() !== contentOnFocus.current) {
                onBlur();
            }
        },
        editorProps: {
            handleDrop: (_view, event, _slice, moved) => {
                if (moved || !uploadImageRef.current || !event.dataTransfer?.files?.length)
                    return false;
                const file = event.dataTransfer.files[0];
                if (file.type.startsWith('image/')) {
                    event.preventDefault();
                    handleImageFileDrop(file);
                    return true;
                }
                return false;
            },
            handlePaste: (_view, event) => {
                if (!uploadImageRef.current || !event.clipboardData?.files?.length)
                    return false;
                const file = event.clipboardData.files[0];
                if (file.type.startsWith('image/')) {
                    handleImageFileDrop(file);
                    return true;
                }
                return false;
            },
        },
    });
    // Keep editorRef in sync so handleImageFileDrop can access it
    useEffect(() => {
        editorRef.current = editor;
    }, [editor]);
    // Sync external editorRef prop so parent can imperatively access the editor
    useEffect(() => {
        if (!externalEditorRef)
            return;
        if (typeof externalEditorRef === 'function') {
            externalEditorRef(editor);
        }
        else {
            externalEditorRef.current = editor;
        }
    }, [editor, externalEditorRef]);
    useEffect(() => {
        if (editor && content !== editor.getHTML() && content !== lastSetContentRef.current) {
            lastSetContentRef.current = content;
            pendingContentRef.current = content;
            queueMicrotask(() => {
                if (editor && !editor.isDestroyed) {
                    editor.commands.setContent(content, false);
                    lastSetContentRef.current = editor.getHTML();
                }
                pendingContentRef.current = null;
            });
        }
    }, [content, editor]);
    // Update editable state when prop changes
    useEffect(() => {
        if (editor) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);
    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }
        editor.commands.setCoPilotGhost(ghostText ?? null, {
            mode: ghostMode ?? 'accept',
        });
    }, [editor, ghostText, ghostMode]);
    const handleContainerClick = (e) => {
        // Focus the editor when clicking in the container area
        // but not if clicking on an interactive element (button, link, etc.)
        const target = e.target;
        const isInteractive = target.closest('button, a, input, select, textarea, [role="button"]');
        if (!isInteractive && editor && !editor.isFocused) {
            editor.commands.focus('end');
        }
    };
    return (_jsxs("div", { className: `prose prose-sm max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl dark:prose-invert relative cursor-text ${className}`, onClick: handleContainerClick, children: [showStaticToolbar && _jsx(EditorToolbar, { editor: editor, showAISuggestionHint: false, onImageUpload: handleImageUpload }), _jsx(EditorContent, { editor: editor }), showFloatingToolbar && !showStaticToolbar && _jsx(FloatingToolbar, { editor: editor, onImageUpload: handleImageUpload, isImageModalOpen: isImageModalOpen, onVideoUpload: handleVideoUpload, isVideoModalOpen: isVideoModalOpen, onAIRewrite: onAIRewrite }), _jsx(ImageUploadModal, { isOpen: isImageModalOpen, onClose: handleCloseImageModal, onImageSelect: handleImageSelectWithCallback, uploadImage: uploadImage }), _jsx(VideoUploadModal, { isOpen: isVideoModalOpen, onClose: handleCloseVideoModal, onVideoSelect: handleVideoSelectWithCallback })] }));
};
export { useEditor } from '@tiptap/react';
//# sourceMappingURL=TipTapEditor.js.map